import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { RefreshCw, CheckCircle, XCircle, Plus, Upload } from 'lucide-react';
import posts from '@/shared/api/posts';
import { BLOG_POSTS } from '@/mock';

const SitemapAutoUpdater = () => {
    const [loading, setLoading] = useState(false);
    const [dbArticles, setDbArticles] = useState([]);
    const [mockArticles, setMockArticles] = useState([]);
    const [missingArticles, setMissingArticles] = useState([]);
    const [currentSitemap, setCurrentSitemap] = useState('');
    const [updatedSitemap, setUpdatedSitemap] = useState('');
    const [stats, setStats] = useState({});

    useEffect(() => {
        loadAllArticles();
        loadCurrentSitemap();
    }, []);

    const loadAllArticles = async () => {
        try {
            setLoading(true);
            
            // Загружаем статьи из базы данных
            let dbArticlesList = [];
            try {
                const dbResponse = await posts.getPosts(1, 100, true);
                dbArticlesList = dbResponse?.posts || [];
            } catch (err) {
                console.warn('Could not load database articles:', err);
                // Пробуем опубликованные статьи
                try {
                    const publishedResponse = await posts.getPublishedPosts(1, 100);
                    dbArticlesList = publishedResponse?.posts || [];
                } catch (err2) {
                    console.warn('Could not load published articles either:', err2);
                }
            }
            
            setDbArticles(dbArticlesList);
            
            // Mock статьи
            setMockArticles(BLOG_POSTS);
            
            // Объединяем все статьи
            const allArticles = [
                ...dbArticlesList.map(article => ({
                    ...article,
                    source: 'database',
                    slug: article.slug || article.id
                })),
                ...BLOG_POSTS.map(article => ({
                    ...article,
                    source: 'mock',
                    slug: article.id
                }))
            ];
            
            // Убираем дубликаты
            const uniqueArticles = allArticles.filter((article, index, self) => 
                index === self.findIndex(a => a.slug === article.slug)
            );
            
            // Находим отсутствующие статьи
            const missing = uniqueArticles.filter(article => 
                typeof currentSitemap === 'string' && !currentSitemap.includes(`/article/${article.slug}`)
            );
            
            setMissingArticles(missing);
            
            // Статистика
            const currentSitemapUrls = (typeof currentSitemap === 'string' ? (currentSitemap.match(/<loc>/g) || []) : []).length;
            const staticPagesCount = 3; // /, /articles, /tests
            
            setStats({
                totalDb: dbArticlesList.length,
                totalMock: BLOG_POSTS.length,
                totalAll: uniqueArticles.length,
                missing: missing.length,
                inSitemap: Math.max(0, currentSitemapUrls - staticPagesCount)
            });
            
            // Автоматически генерируем обновленный sitemap
            if (missing.length > 0) {
                generateUpdatedSitemap();
            }
            
        } catch (error) {
            console.error('Error loading articles:', error);
            toast.error('Ошибка загрузки статей');
        } finally {
            setLoading(false);
        }
    };

    const loadCurrentSitemap = async () => {
        try {
            const response = await fetch('/sitemap.xml');
            const sitemapText = await response.text();
            setCurrentSitemap(sitemapText);
        } catch (error) {
            console.error('Error loading current sitemap:', error);
        }
    };

    const generateUpdatedSitemap = () => {
        const baseUrl = 'https://cope.by';
        
        // Все статьи из всех источников
        const allArticles = [
            ...dbArticles.map(article => ({
                slug: article.slug || article.id,
                title: article.title,
                lastmod: article.updatedAt || article.createdAt || '2024-01-01',
                source: 'database'
            })),
            ...mockArticles.map(article => ({
                slug: article.id,
                title: article.title,
                lastmod: '2024-01-01',
                source: 'mock'
            }))
        ];

        // Убираем дубликаты по slug
        const uniqueArticles = allArticles.filter((article, index, self) => 
            index === self.findIndex(a => a.slug === article.slug)
        );

        // Статические страницы
        const staticPages = [
            { url: '/', priority: 1.0, changefreq: 'daily', lastmod: '2024-01-01' },
            { url: '/articles', priority: 0.8, changefreq: 'weekly', lastmod: '2024-01-01' },
            { url: '/tests', priority: 0.8, changefreq: 'weekly', lastmod: '2024-01-01' },
        ];

        // Генерируем XML
        const urls = [
            ...staticPages.map(page => formatUrl(page.url, page.priority, page.changefreq, page.lastmod)),
            ...uniqueArticles.map(article => formatUrl(
                `/article/${article.slug}`, 
                0.9, 
                'weekly', 
                article.lastmod
            ))
        ];

        const newSitemap = `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
${urls.join('\n')}
</urlset>`;

        setUpdatedSitemap(newSitemap);
        return newSitemap;
    };

    const formatUrl = (path, priority, changefreq, lastmod) => {
        const url = `https://cope.by${path}`;
        const lastmodStr = lastmod ? new Date(lastmod).toISOString().split('T')[0] : '2024-01-01';
        
        return `  <url>
    <loc>${url}</loc>
    <lastmod>${lastmodStr}</lastmod>
    <changefreq>${changefreq}</changefreq>
    <priority>${priority}</priority>
  </url>`;
    };

    const handleUpdateSitemap = () => {
        setLoading(true);
        
        try {
            const newSitemap = generateUpdatedSitemap();
            
            // Копируем в буфер обмена
            navigator.clipboard.writeText(newSitemap);
            
            toast.success('✅ Sitemap обновлен и скопирован в буфер обмена!');
            
            // Обновляем статистику
            loadAllArticles();
            loadCurrentSitemap();
            
        } catch (error) {
            console.error('Error updating sitemap:', error);
            toast.error('Ошибка обновления sitemap');
        } finally {
            setLoading(false);
        }
    };

    const downloadSitemap = () => {
        const sitemap = updatedSitemap || generateUpdatedSitemap();
        const blob = new Blob([sitemap], { type: 'application/xml' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'sitemap.xml';
        a.click();
        URL.revokeObjectURL(url);
        toast.success('📁 Sitemap скачан');
    };

    return (
        <div className="space-y-6">
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <RefreshCw className="w-5 h-5" />
                        Автообновление Sitemap
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                        <div className="text-center p-3 bg-blue-50 rounded">
                            <div className="text-xl font-bold text-blue-600">{stats.totalDb || 0}</div>
                            <div className="text-xs text-gray-600">Статей в БД</div>
                        </div>
                        <div className="text-center p-3 bg-green-50 rounded">
                            <div className="text-xl font-bold text-green-600">{stats.totalMock || 0}</div>
                            <div className="text-xs text-gray-600">Mock статей</div>
                        </div>
                        <div className="text-center p-3 bg-purple-50 rounded">
                            <div className="text-xl font-bold text-purple-600">{stats.totalAll || 0}</div>
                            <div className="text-xs text-gray-600">Всего статей</div>
                        </div>
                        <div className="text-center p-3 bg-orange-50 rounded">
                            <div className="text-xl font-bold text-orange-600">{stats.missing || 0}</div>
                            <div className="text-xs text-gray-600">Отсутствуют</div>
                        </div>
                        <div className="text-center p-3 bg-gray-50 rounded">
                            <div className="text-xl font-bold text-gray-600">{stats.inSitemap || 0}</div>
                            <div className="text-xs text-gray-600">В sitemap</div>
                        </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                        <Button onClick={handleUpdateSitemap} disabled={loading}>
                            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                            {loading ? 'Обновление...' : '🔄 Обновить Sitemap'}
                        </Button>
                        
                        <Button onClick={downloadSitemap} variant="outline">
                            <Upload className="w-4 h-4 mr-2" />
                            💾 Скачать XML
                        </Button>
                        
                        <Button onClick={loadAllArticles} variant="outline">
                            <RefreshCw className="w-4 h-4 mr-2" />
                            📊 Обновить статистику
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Отсутствующие статьи */}
            {missingArticles.length > 0 && (
                <Card className="border-orange-200">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-orange-600">
                            <Plus className="w-5 h-5" />
                            Отсутствующие статьи ({missingArticles.length})
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className="text-sm text-orange-700 mb-4">
                            Эти статьи есть в системе, но отсутствуют в текущем sitemap:
                        </p>
                        <div className="space-y-2">
                            {missingArticles.map((article, index) => (
                                <div key={index} className="flex items-center justify-between p-3 bg-orange-50 rounded border border-orange-200">
                                    <div>
                                        <p className="font-medium">{article.title}</p>
                                        <p className="text-sm text-gray-600">
                                            /article/{article.slug} 
                                            <span className="ml-2 text-xs bg-orange-200 px-2 py-1 rounded">
                                                {article.source}
                                            </span>
                                        </p>
                                    </div>
                                    <Plus className="w-5 h-5 text-orange-500" />
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Предпросмотр обновленного sitemap */}
            {updatedSitemap && (
                <Card>
                    <CardHeader>
                        <CardTitle>📄 Обновленный Sitemap</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="mb-4">
                            <p><strong>Всего URL:</strong> {(typeof updatedSitemap === 'string' ? (updatedSitemap.match(/<loc>/g) || []) : []).length}</p>
                            <p><strong>Размер:</strong> {typeof updatedSitemap === 'string' ? updatedSitemap.length : 0} символов</p>
                        </div>
                        
                        <details>
                            <summary>Показать XML</summary>
                            <div className="mt-4">
                                <pre className="bg-gray-50 p-4 rounded text-xs overflow-auto max-h-96 whitespace-pre-wrap font-mono border">
                                    {updatedSitemap}
                                </pre>
                            </div>
                        </details>
                        
                        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded">
                            <p className="text-green-800 text-sm">
                                <CheckCircle className="inline w-4 h-4 mr-1" />
                                <strong>Готово!</strong> Sitemap обновлен со всеми статьями. 
                                Скопируйте XML и замените содержимое файла <code className="bg-green-100 px-1">public/sitemap.xml</code>
                            </p>
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
};

export default SitemapAutoUpdater;
